<template>
  <v-menu
    v-model="isMenuOpen"
    :close-on-content-click="false"
    offset="10"
    :content-props="{ style: { 'min-width': 0 }}"
  >
    <template v-slot:activator="{ props }">
      <AppTextField
        :model-value="computedInputValue"
        :label="label"
        :placeholder="computedInputPlaceholder"
        clearable
        readonly
        v-bind="props"
        @clear="onClear"
      />
    </template>

    <v-date-picker
      v-model="internalValue"
      multiple="range"
      color="primary"
    >
      <template #header>
        <p class="mx-6 my-2 text--large text--regular">
          {{ datePickerHeader }}
        </p>
      </template>

      <template #actions>
        <app-button type="outlined" @click="isMenuOpen = false">
          {{ $t("common.cancel") }}
        </app-button>
        <app-button :disabled="internalValue.length ===0" @click="onValidate">
          {{ $t("common.validate") }}
        </app-button>
      </template>
    </v-date-picker>
  </v-menu>
</template>

<script lang="ts">
import dayjs from 'dayjs'
import { defineComponent, PropType } from 'vue'

import AppTextField from '@/common/AppTextField.vue'
import { getFirstAndLast } from '@/common/utils/array'
import { ISOToShortenedDate, formatDateValue, extractOnlyDateFromDateTime } from '@/common/utils/dates'

export default defineComponent({
  name: 'AppDateRangePicker',
  components: { AppTextField },
  inheritAttrs: false,
  emits: ['update:model-value'],
  props: {
    /**
     * @model
     * Value of Date Range picker
     * @values [Date, Date]
     */
    modelValue: {
      type: Array as PropType<Array<Date>>,
      required: true,
    },
    /**
     * Label of the picker
     */
    label: {
      type: String,
      default: '',
    },
  },
  data (): {
    internalValue: Array<Date>,
    isMenuOpen: boolean,
  } {
    return {
      internalValue: [],
      isMenuOpen: false,
    }
  },
  computed: {
    computedInputValue () {
      return this.modelValue.map(date => formatDateValue(date)).join(' - ')
    },
    computedInputPlaceholder () {
      const placeholderStartDate = ISOToShortenedDate(dayjs().subtract(1, 'months').format('YYYY-MM-DD'))
      const placeholderEndDate = ISOToShortenedDate(dayjs().format('YYYY-MM-DD'))

      return `${placeholderStartDate} - ${placeholderEndDate}`
    },
    datePickerHeader () {
      const rangeValues = getFirstAndLast<Date>(this.internalValue)
      return rangeValues.map(date => formatDateValue(date)).join(' - ')
    },
  },
  methods: {
    onClear () {
      this.$emit('update:model-value', [])
      this.isMenuOpen = false
    },
    onValidate () {
      const rangeValues = getFirstAndLast<Date>(this.internalValue).map((value) => extractOnlyDateFromDateTime(value))
      this.$emit('update:model-value', rangeValues)
      this.isMenuOpen = false
    },
  },
  watch: {
    isMenuOpen (value) {
      if (value) {
        const [startDate, endDate] = this.modelValue

        if (!startDate) {
          this.internalValue = []
        } else if (!endDate) {
          this.internalValue = [dayjs(startDate).toDate()]
        } else {
          const start = dayjs(startDate);
          const end = dayjs(endDate);
          const numberOfDays = end.diff(start, 'day') + 1;

          this.internalValue =  Array.from(
            { length: numberOfDays },
            (_, index) => start.add(index, 'day').toDate(),
          )
        }
      }
    },
  },
})
</script>
<style scoped lang="scss">
:deep(.v-label) {
  color:rgb(var(--v-theme-grey-500));
}
</style>